/* More Designs carousel: progress bar + keep selected phone model when opening another design */
if (!customElements.get('more-designs')) {
  customElements.define(
    'more-designs',
    class MoreDesigns extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-md-track]');
        this.bar = this.querySelector('[data-md-progress]');
        this.variantTitle = this.dataset.currentVariantTitle || '';
        this.initialTitle = this.variantTitle;

        if (this.track && this.bar) {
          this.onScroll = this.updateProgress.bind(this);
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.updateProgress();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event && event.data && event.data.variant;
            if (variant && variant.title) this.variantTitle = variant.title;
          });
        }

        this.addEventListener('click', this.onClick.bind(this));
        this.setupDesktopScroll();
      }

      animateTo(target) {
        const track = this.track;
        const start = track.scrollLeft;
        const distance = target - start;
        const duration = 350;
        const token = (this.animToken = (this.animToken || 0) + 1);
        if (Math.abs(distance) < 1) return;
        let startTime = null;
        const step = (time) => {
          if (token !== this.animToken) return;
          if (startTime === null) startTime = time;
          const p = Math.min(1, (time - startTime) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          track.scrollLeft = start + distance * eased;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        // if animation frames do not run (background tab), still end at the target
        setTimeout(() => {
          if (token === this.animToken && Math.abs(track.scrollLeft - target) > 1) track.scrollLeft = target;
        }, duration + 150);
      }

      /* Desktop: a mouse cannot swipe the track, so add drag-to-scroll and arrow buttons */
      setupDesktopScroll() {
        const track = this.track;
        if (!track || this.desktopScrollReady) return;
        this.desktopScrollReady = true;

        // arrows (shown by CSS only on devices with a mouse, and only when the track overflows)
        const wrap = document.createElement('div');
        wrap.className = 'more-designs__viewport';
        track.parentNode.insertBefore(wrap, track);
        wrap.appendChild(track);
        const makeArrow = (dir) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = `more-designs__arrow more-designs__arrow--${dir}`;
          b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
          b.innerHTML =
            dir === 'prev'
              ? '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>'
              : '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
          b.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            // move by the number of whole cards that fit, aligned to a card edge
            const first = track.querySelector('.more-designs__item');
            const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
            const unit = first ? first.getBoundingClientRect().width + gap : 100;
            const perView = Math.max(1, Math.floor((track.clientWidth + gap) / unit));
            const max = track.scrollWidth - track.clientWidth;
            const current = Math.round(track.scrollLeft / unit);
            const target = Math.min(max, Math.max(0, (current + (dir === 'prev' ? -perView : perView)) * unit));
            // own animation via scrollLeft: works the same in every browser (native smooth
            // scrolling is ignored or reverted by Safari on some scroll containers)
            this.animateTo(target);
          });
          wrap.appendChild(b);
          return b;
        };
        this.prevBtn = makeArrow('prev');
        this.nextBtn = makeArrow('next');
        const updateArrows = () => {
          const max = track.scrollWidth - track.clientWidth;
          this.prevBtn.hidden = max <= 2 || track.scrollLeft <= 2;
          this.nextBtn.hidden = max <= 2 || track.scrollLeft >= max - 2;
        };
        track.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);
        updateArrows();
        setTimeout(updateArrows, 600);

        // drag with the mouse (touch keeps native swiping)
        let down = false;
        let startX = 0;
        let startLeft = 0;
        this.dragged = false;
        track.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          down = true;
          this.dragged = false;
          this.animToken = (this.animToken || 0) + 1; // stop an arrow animation
          startX = e.clientX;
          startLeft = track.scrollLeft;
        });
        track.addEventListener('pointermove', (e) => {
          if (!down) return;
          const dx = e.clientX - startX;
          if (!this.dragged && Math.abs(dx) > 5) {
            this.dragged = true;
            track.classList.add('is-dragging');
            try {
              track.setPointerCapture(e.pointerId);
            } catch (err) {}
          }
          if (this.dragged) track.scrollLeft = startLeft - dx;
        });
        const end = () => {
          if (!down) return;
          down = false;
          track.classList.remove('is-dragging');
        };
        track.addEventListener('pointerup', end);
        track.addEventListener('pointercancel', end);
        track.addEventListener('dragstart', (e) => e.preventDefault());
        // a drag must not open the design under the mouse
        track.addEventListener(
          'click',
          (e) => {
            if (this.dragged) {
              e.preventDefault();
              e.stopImmediatePropagation();
              this.dragged = false;
            }
          },
          true
        );
      }

      disconnectedCallback() {
        if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
        if (this.onScroll) window.removeEventListener('resize', this.onScroll);
        if (this.unsubscribe) this.unsubscribe();
      }

      updateProgress() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const visible = this.track.clientWidth / this.track.scrollWidth;
        const width = Math.max(visible, 0.1) * 100;
        this.bar.style.width = `${width}%`;
        const ratio = max > 0 ? this.track.scrollLeft / max : 0;
        const travel = (100 / width - 1) * 100;
        this.bar.style.transform = `translateX(${ratio * travel}%)`;
        this.querySelector('.more-designs__progress').hidden = max <= 0;
      }

      currentTitle() {
        const scope = this.closest('product-info') || document;
        const picker = scope.querySelector('variant-selects');
        if (!picker) return '';
        const values = [];
        picker.querySelectorAll('select').forEach((select) => values.push(select.value));
        picker.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) values.push(checked.value);
        });
        return values.join(' / ');
      }

      async onClick(event) {
        const link = event.target.closest('a.more-designs__item');
        if (!link || link.hasAttribute('aria-current')) return;
        // Read the model at click time: right after a model change the page update
        // (and the variantChange event) can still be on its way
        const title = this.currentTitle() || this.variantTitle;
        // Server-rendered link already matches the model the page was loaded with
        if (!title || title === this.initialTitle) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

        event.preventDefault();
        const handle = link.dataset.mdHandle;
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        try {
          const response = await fetch(`${root}products/${handle}.js`);
          const product = await response.json();
          const match = product.variants.find((v) => v.title === title);
          const base = link.href.split('?')[0];
          window.location.href = match ? `${base}?variant=${match.id}` : base;
        } catch (e) {
          window.location.href = link.href;
        }
      }
    }
  );
}